import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InstituteAutomationService, OverviewListType, OverviewListItem } from '../instituteAutomationService';

const SEARCH_THRESHOLD = 12; // show the search box only when the list is longer than this

/** Drill-down list opened by clicking an Overview stat card. */
const StatListModal: React.FC<{ open: boolean; type: OverviewListType | null; title: string; onClose: () => void }> = ({ open, type, title, onClose }) => {
    const [items, setItems] = useState<OverviewListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [query, setQuery] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!open || !type) return;
        let cancelled = false;
        setLoading(true); setError(false); setQuery('');
        InstituteAutomationService.getOverviewList(type)
            .then((d) => { if (!cancelled) setItems(d); })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, type]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) => it.title.toLowerCase().includes(q) || it.subtitle.toLowerCase().includes(q));
    }, [items, query]);

    if (!open) return null;

    const goto = (it: OverviewListItem) => {
        onClose();
        navigate(`/institute-automation-report-preview?institute_id=${encodeURIComponent(it.instituteId)}&name=${encodeURIComponent(it.title)}`);
    };

    return (
        <>
            <div className="modal-backdrop fade show" />
            <div className="modal fade show d-block" tabIndex={-1} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content border-0 shadow-lg rounded-3">
                        <div className="modal-header border-0 pb-2">
                            <h5 className="modal-title font-16 font-600 text-dark-blue d-flex align-items-center gap-2">
                                {title}
                                <span className="badge bg-light text-secondary rounded-pill font-11 px-2 py-0.5">{items.length}</span>
                            </h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
                        </div>
                        <div className="modal-body pt-1">
                            {items.length > SEARCH_THRESHOLD && (
                                <div className="input-group input-group-sm mb-3">
                                    <span className="input-group-text bg-white border-end-0 text-secondary"><i className="bi bi-search" /></span>
                                    <input type="text" className="form-control form-control-sm font-13 border-start-0" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ boxShadow: 'none' }} autoFocus />
                                </div>
                            )}
                            {loading ? (
                                <div className="text-center text-secondary py-4"><span className="spinner-border spinner-border-sm me-2" />Loading…</div>
                            ) : error ? (
                                <div className="text-center text-secondary py-4 font-12">Couldn’t load the list.</div>
                            ) : filtered.length === 0 ? (
                                <div className="text-center text-secondary py-4 font-12">{items.length === 0 ? 'Nothing here yet.' : 'No matches.'}</div>
                            ) : (
                                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                    {filtered.map((it) => (
                                        <button type="button" key={it.id + it.subtitle} onClick={() => goto(it)}
                                            className="w-100 text-start d-flex align-items-center justify-content-between border-bottom border-light py-2 px-2 bg-transparent border-0"
                                            style={{ cursor: 'pointer' }}>
                                            <div className="min-w-0">
                                                <div className="font-13 font-600 text-dark text-truncate">{it.title}</div>
                                                <div className="font-11 text-secondary text-truncate">{it.subtitle}</div>
                                            </div>
                                            <span className="text-secondary font-11 flex-shrink-0 ms-2">👁 Preview</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StatListModal;
